import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  api, ApiFailure, lineTotalOf, quoteInvoice,
  PAYMENT_MODE_LABEL, SETTLEMENT_LABEL,
  type DraftItem, type InvoiceDetail, type InvoiceListRow, type InvoiceStatus,
  type PaymentMode, type PatientMatch, type Settlement,
} from '../api'
import { useAuth } from '../auth'

const BLANK_LINE: DraftItem = {
  description: '', quantity: '1', unitPrice: '', discount: '0', taxRate: '0',
}

/** Modes the desk can take money in against a bill at the counter. */
const COUNTER_MODES: PaymentMode[] = ['cash', 'upi', 'card', 'net_banking', 'offline']

function rupees(v: string | number): string {
  return `₹${Number(v).toFixed(2)}`
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

function settlementPill(s: Settlement): string {
  return s === 'paid' ? 'pill on' : s === 'partial' ? 'pill warn' : 'pill off'
}

function statusPill(s: InvoiceStatus): string {
  return s === 'finalised' ? 'pill on' : s === 'void' ? 'pill off' : 'pill warn'
}

export default function Billing() {
  const { user } = useAuth()
  const canRefund = user?.role === 'admin'
  const [rows, setRows] = useState<InvoiceListRow[]>([])
  const [filter, setFilter] = useState<'all' | 'owing' | InvoiceStatus>('owing')
  const [opened, setOpened] = useState<InvoiceDetail | null>(null)
  const [raising, setRaising] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null)

  const load = useCallback(async () => {
    setBusy(true)
    try {
      // "Owing" is a view over finalised bills rather than a server filter -
      // the balance is computed, not stored, so the server has no status to
      // query for it.
      const path = filter === 'all' || filter === 'owing'
        ? '/api/invoices?limit=200'
        : `/api/invoices?status=${filter}&limit=200`
      const r = await api.get<{ invoices: InvoiceListRow[] }>(path)
      setRows(filter === 'owing'
        ? r.invoices.filter((i) => i.status === 'finalised' && Number(i.outstanding) > 0)
        : r.invoices)
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not load bills.' })
    } finally {
      setBusy(false)
    }
  }, [filter])

  useEffect(() => { void load() }, [load])

  async function open(id: string) {
    try {
      setOpened(await api.get<InvoiceDetail>(`/api/invoices/${id}`))
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not open the bill.' })
    }
  }

  const owed = rows.reduce((s, r) => s + Number(r.outstanding), 0)

  return (
    <>
      <div className="page-head">
        <h2>Billing</h2>
        <p>Raise an itemised bill, take it in instalments, and see what is still owed.</p>
      </div>

      {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

      <div className="panel">
        <div className="records-views">
          {([['owing', 'Outstanding'], ['draft', 'Drafts'], ['finalised', 'Finalised'],
            ['void', 'Void'], ['all', 'All']] as const).map(([key, label]) => (
            <button key={key} className={filter === key ? 'chip is-on' : 'chip'}
              onClick={() => setFilter(key)}>{label}</button>
          ))}
          <span className="spacer" />
          {busy && <span className="panel-note" style={{ margin: 0 }}>Loading…</span>}
          <button onClick={() => setRaising(true)}>Raise a bill</button>
        </div>
      </div>

      <div className="panel">
        <h3>
          {rows.length} bill{rows.length === 1 ? '' : 's'}
          {owed > 0 && (
            <span className="panel-note" style={{ margin: 0 }}> · {rupees(owed)} outstanding</span>
          )}
        </h3>
        {rows.length === 0 && !busy
          ? <div className="empty">Nothing here.</div>
          : (
            <div className="table-wrap">
              <table className="records-table">
                <thead>
                  <tr>
                    <th>Bill</th><th>Patient</th><th className="num">Total</th>
                    <th className="num">Paid</th><th className="num">Outstanding</th>
                    <th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="nowrap">
                        <div className="mono">{r.invoiceNo}</div>
                        <div className="rec-code">{fmtDateTime(r.createdAt)}</div>
                      </td>
                      <td className="nowrap">
                        <div className="rec-name">{r.patientName}</div>
                        <div className="rec-code">{r.patientCode}</div>
                      </td>
                      <td className="num">{rupees(r.total)}</td>
                      <td className="num">
                        {rupees(r.paid)}
                        {Number(r.refunded) > 0 && (
                          <div className="rec-code">less {rupees(r.refunded)} refunded</div>
                        )}
                      </td>
                      <td className="num">
                        {Number(r.outstanding) > 0 ? <strong>{rupees(r.outstanding)}</strong> : '—'}
                      </td>
                      <td className="nowrap">
                        <span className={statusPill(r.status)}>{r.status}</span>{' '}
                        {r.status === 'finalised' && (
                          <span className={settlementPill(r.settlement)}>
                            {SETTLEMENT_LABEL[r.settlement]}
                          </span>
                        )}
                      </td>
                      <td className="nowrap">
                        <button className="link" onClick={() => void open(r.id)}>Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {raising && (
        <RaiseBill
          onClose={() => setRaising(false)}
          onRaised={async (detail) => {
            setRaising(false)
            setOpened(detail)
            await load()
          }}
        />
      )}

      {opened && (
        <BillModal
          detail={opened}
          canRefund={canRefund}
          onClose={() => setOpened(null)}
          onChanged={async (next) => { setOpened(next); await load() }}
          onError={(text) => setMsg({ kind: 'error', text })}
        />
      )}
    </>
  )
}

/**
 * The four figures of a bill being typed, in the same order and wording the
 * finalised bill uses, so the desk reads one vocabulary throughout.
 */
function BillQuote({ quote }: { quote: ReturnType<typeof quoteInvoice> }) {
  return (
    <div className="bill-total-row">
      <span>Items {rupees(quote.subtotal)}</span>
      {quote.discountTotal > 0 && <span>discount − {rupees(quote.discountTotal)}</span>}
      {quote.taxTotal > 0 && <span>tax {rupees(quote.taxTotal)}</span>}
      <strong>Total {rupees(quote.total)}</strong>
    </div>
  )
}

/** The line-item editor, shared by raising a bill and re-pricing a draft. */
function LineEditor({ lines, setLines, disabled }: {
  lines: DraftItem[]
  setLines: (next: DraftItem[]) => void
  disabled?: boolean
}) {
  function set(i: number, patch: Partial<DraftItem>) {
    setLines(lines.map((l, n) => (n === i ? { ...l, ...patch } : l)))
  }

  return (
    <div className="table-wrap">
      <table className="records-table bill-lines">
        <thead>
          <tr>
            <th>Item</th><th className="num">Qty</th><th className="num">Rate</th>
            <th className="num">Discount</th><th className="num">Tax %</th>
            <th className="num">Amount</th><th></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td>
                <input value={l.description} disabled={disabled} placeholder="Consultation, dressing, injection…"
                  onChange={(e) => set(i, { description: e.target.value })} />
              </td>
              <td className="num">
                <input className="cell-num" type="number" min="1" step="1" value={l.quantity} disabled={disabled}
                  onChange={(e) => set(i, { quantity: e.target.value })} />
              </td>
              <td className="num">
                <input className="cell-num" type="number" min="0" step="0.01" value={l.unitPrice} disabled={disabled}
                  onChange={(e) => set(i, { unitPrice: e.target.value })} />
              </td>
              <td className="num">
                <input className="cell-num" type="number" min="0" step="0.01" value={l.discount} disabled={disabled}
                  onChange={(e) => set(i, { discount: e.target.value })} />
              </td>
              <td className="num">
                <input className="cell-num" type="number" min="0" max="100" step="0.01" value={l.taxRate} disabled={disabled}
                  onChange={(e) => set(i, { taxRate: e.target.value })} />
              </td>
              <td className="num">{rupees(lineTotalOf(l))}</td>
              <td className="nowrap">
                {lines.length > 1 && !disabled && (
                  <button type="button" className="link"
                    onClick={() => setLines(lines.filter((_, n) => n !== i))}>Remove</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!disabled && (
        <button type="button" className="link" onClick={() => setLines([...lines, { ...BLANK_LINE }])}>
          + Add a line
        </button>
      )}
    </div>
  )
}

function RaiseBill({ onClose, onRaised }: {
  onClose: () => void
  onRaised: (detail: InvoiceDetail) => void | Promise<void>
}) {
  const [query, setQuery] = useState('')
  const [matches, setMatches] = useState<PatientMatch[]>([])
  const [patient, setPatient] = useState<PatientMatch | null>(null)
  const [lines, setLines] = useState<DraftItem[]>([{ ...BLANK_LINE }])
  const [billDiscount, setBillDiscount] = useState('0')
  const [discountReason, setDiscountReason] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (patient || query.trim().length < 2) { setMatches([]); return }
    const t = setTimeout(() => {
      void (async () => {
        try {
          const r = await api.get<{ patients: PatientMatch[] }>(
            `/api/patients/search?q=${encodeURIComponent(query.trim())}`,
          )
          setMatches(r.patients)
        } catch { /* a failed lookup shows as no matches */ }
      })()
    }, 250)
    return () => clearTimeout(t)
  }, [query, patient])

  const quote = quoteInvoice(lines, billDiscount)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!patient) { setError('Choose the patient first.'); return }
    setBusy(true); setError(null)
    try {
      const detail = await api.post<InvoiceDetail>('/api/invoices', {
        patientId: patient.id,
        items: lines.map((l) => ({
          description: l.description.trim(),
          quantity: Number(l.quantity) || 1,
          unitPrice: l.unitPrice || '0',
          discount: l.discount || '0',
          taxRate: l.taxRate || '0',
        })),
        discountTotal: billDiscount || '0',
        discountReason: discountReason.trim() || undefined,
        notes: notes.trim() || undefined,
      })
      await onRaised(detail)
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'Could not raise the bill.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal bill-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Raise a bill</h3>
        {error && <div className="msg error">{error}</div>}

        <form onSubmit={(e) => void submit(e)}>
          {patient ? (
            <div className="field">
              <label>Patient</label>
              <div>
                <strong>{patient.fullName}</strong>{' '}
                <span className="panel-note">{[patient.patientCode, patient.mobile].filter(Boolean).join(' · ')}</span>{' '}
                <button type="button" className="link" onClick={() => { setPatient(null); setQuery('') }}>
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div className="field">
              <label htmlFor="bill-patient">Patient</label>
              <input id="bill-patient" value={query} autoFocus
                placeholder="Name, mobile or patient code"
                onChange={(e) => setQuery(e.target.value)} />
              {matches.length > 0 && (
                <div className="modal-matches">
                  {matches.slice(0, 8).map((m) => (
                    <button type="button" key={m.id} className="ac-item"
                      onClick={() => { setPatient(m); setMatches([]) }}>
                      <strong>{m.fullName}</strong>
                      <span className="rec-code">{[m.patientCode, m.mobile].filter(Boolean).join(' · ')}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <h4 style={{ margin: '14px 0 6px' }}>Items</h4>
          <LineEditor lines={lines} setLines={setLines} />

          <div className="grid two" style={{ marginTop: 12 }}>
            <div className="field">
              <label htmlFor="bill-disc">Discount on the whole bill</label>
              <input id="bill-disc" type="number" min="0" step="0.01" value={billDiscount}
                onChange={(e) => setBillDiscount(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="bill-disc-why">Reason for the discount</label>
              <input id="bill-disc-why" value={discountReason}
                placeholder={Number(billDiscount) > 0 ? 'Required' : 'Not needed'}
                onChange={(e) => setDiscountReason(e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label htmlFor="bill-notes">Notes</label>
            <input id="bill-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <BillQuote quote={quote} />

          <p className="panel-note">
            Raised as a draft. Read it back to the patient, then finalise it — after that the
            figures are fixed and it can take payment.
          </p>

          <div className="actions">
            <button type="button" className="secondary" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={busy}>{busy ? 'Raising…' : 'Raise as draft'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function BillModal({ detail, canRefund, onClose, onChanged, onError }: {
  detail: InvoiceDetail
  canRefund: boolean
  onClose: () => void
  onChanged: (next: InvoiceDetail) => void | Promise<void>
  onError: (text: string) => void
}) {
  const { invoice, patient, items, payments, refunds, totals } = detail
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [lines, setLines] = useState<DraftItem[]>([])
  const [billDiscount, setBillDiscount] = useState(invoice.discountTotal)
  const [discountReason, setDiscountReason] = useState(invoice.discountReason ?? '')

  // Taking money
  const outstanding = Number(totals.outstanding)
  const [amount, setAmount] = useState(totals.outstanding)
  const [mode, setMode] = useState<PaymentMode>('cash')
  const [reference, setReference] = useState('')
  const [cashReceived, setCashReceived] = useState('')

  // Refunding
  const [refundOf, setRefundOf] = useState<string | null>(null)
  const [refundAmount, setRefundAmount] = useState('')
  const [refundReason, setRefundReason] = useState('')

  useEffect(() => { setAmount(totals.outstanding) }, [totals.outstanding])

  function startEditing() {
    setLines(items.map((i) => ({
      description: i.description,
      quantity: String(i.quantity),
      unitPrice: i.unitPrice,
      discount: i.discount,
      taxRate: i.taxRate,
    })))
    setBillDiscount(invoice.discountTotal)
    setDiscountReason(invoice.discountReason ?? '')
    setEditing(true)
  }

  /** One place for every call this modal makes, so errors land in one spot. */
  async function act(run: () => Promise<InvoiceDetail | null>) {
    setBusy(true); setError(null)
    try {
      const next = await run()
      if (next) await onChanged(next)
    } catch (err) {
      const text = err instanceof ApiFailure ? err.message : 'That did not go through.'
      setError(text)
      if (!(err instanceof ApiFailure)) onError(text)
    } finally {
      setBusy(false)
    }
  }

  const savePricing = () => act(async () => {
    const next = await api.put<InvoiceDetail>(`/api/invoices/${invoice.id}/items`, {
      items: lines.map((l) => ({
        description: l.description.trim(),
        quantity: Number(l.quantity) || 1,
        unitPrice: l.unitPrice || '0',
        discount: l.discount || '0',
        taxRate: l.taxRate || '0',
      })),
      discountTotal: billDiscount || '0',
      discountReason: discountReason.trim() || undefined,
    })
    setEditing(false)
    return next
  })

  const finalise = () => act(() =>
    api.post<InvoiceDetail>(`/api/invoices/${invoice.id}/finalise`))

  const voidBill = () => act(async () => {
    const reason = window.prompt('Why is this bill being voided?')
    if (!reason?.trim()) return null
    return api.post<InvoiceDetail>(`/api/invoices/${invoice.id}/void`, { reason: reason.trim() })
  })

  const takePayment = (e: FormEvent) => {
    e.preventDefault()
    void act(async () => {
      const r = await api.post<{ invoice: InvoiceDetail }>(`/api/invoices/${invoice.id}/payments`, {
        amount,
        mode,
        referenceNo: reference.trim() || undefined,
        cashReceived: mode === 'cash' && cashReceived.trim() !== '' ? cashReceived : undefined,
      })
      setReference(''); setCashReceived('')
      return r.invoice
    })
  }

  const submitRefund = (e: FormEvent) => {
    e.preventDefault()
    void act(async () => {
      const r = await api.post<{ invoice: InvoiceDetail | null }>(
        `/api/payments/${refundOf}/refund`,
        { amount: refundAmount, reason: refundReason.trim() },
      )
      setRefundOf(null); setRefundAmount(''); setRefundReason('')
      return r.invoice
    })
  }

  const change = mode === 'cash' && cashReceived.trim() !== ''
    ? Math.round((Number(cashReceived) - Number(amount)) * 100) / 100
    : null

  const canTakeMoney = invoice.status === 'finalised' && outstanding > 0

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal bill-modal" onClick={(e) => e.stopPropagation()}>
        <h3>
          <span className="mono">{invoice.invoiceNo}</span>{' '}
          <span className={statusPill(invoice.status)}>{invoice.status}</span>{' '}
          {invoice.status === 'finalised' && (
            <span className={settlementPill(totals.settlement)}>{SETTLEMENT_LABEL[totals.settlement]}</span>
          )}
        </h3>
        <div className="panel-note" style={{ margin: '0 0 12px' }}>
          {[patient.fullName, patient.patientCode, patient.mobile].filter(Boolean).join(' · ')} · raised {fmtDateTime(invoice.createdAt)}
        </div>

        {error && <div className="msg error">{error}</div>}
        {invoice.status === 'void' && (
          <div className="msg warn">Voided{invoice.voidReason ? `: ${invoice.voidReason}` : ''}.</div>
        )}

        {editing ? (
          <>
            <LineEditor lines={lines} setLines={setLines} />
            <div className="grid two" style={{ marginTop: 12 }}>
              <div className="field">
                <label>Discount on the whole bill</label>
                <input type="number" min="0" step="0.01" value={billDiscount}
                  onChange={(e) => setBillDiscount(e.target.value)} />
              </div>
              <div className="field">
                <label>Reason</label>
                <input value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} />
              </div>
            </div>
            <BillQuote quote={quoteInvoice(lines, billDiscount)} />
            <div className="actions">
              <button type="button" className="secondary" onClick={() => setEditing(false)}>Cancel</button>
              <button type="button" disabled={busy} onClick={() => void savePricing()}>Save pricing</button>
            </div>
          </>
        ) : (
          <>
            <div className="table-wrap">
              <table className="records-table">
                <thead>
                  <tr>
                    <th>Item</th><th className="num">Qty</th><th className="num">Rate</th>
                    <th className="num">Discount</th><th className="num">Tax</th><th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id}>
                      <td>{i.description}</td>
                      <td className="num">{i.quantity}</td>
                      <td className="num">{rupees(i.unitPrice)}</td>
                      <td className="num">{Number(i.discount) > 0 ? `− ${rupees(i.discount)}` : '—'}</td>
                      <td className="num">
                        {Number(i.taxAmount) > 0 ? `${rupees(i.taxAmount)} (${Number(i.taxRate)}%)` : '—'}
                      </td>
                      <td className="num">{rupees(i.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={5}>Items</td><td className="num">{rupees(invoice.subtotal)}</td></tr>
                  {Number(invoice.discountTotal) > 0 && (
                    <tr>
                      <td colSpan={5}>Discount</td>
                      <td className="num">− {rupees(invoice.discountTotal)}</td>
                    </tr>
                  )}
                  {Number(invoice.taxTotal) > 0 && (
                    <tr><td colSpan={5}>Tax</td><td className="num">{rupees(invoice.taxTotal)}</td></tr>
                  )}
                  <tr className="total">
                    <td colSpan={5}><strong>Total</strong></td>
                    <td className="num"><strong>{rupees(invoice.total)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* The reason belongs to the bill-level discount, not to the
                combined figure in the row above, which also carries whatever
                came off individual lines. */}
            {invoice.discountReason && (
              <p className="panel-note">Discount on the bill: {invoice.discountReason}</p>
            )}
            {invoice.notes && <p className="panel-note">{invoice.notes}</p>}

            {invoice.status === 'draft' && (
              <div className="actions" style={{ marginTop: 12 }}>
                <button type="button" className="secondary" onClick={startEditing}>Edit pricing</button>
                {canRefund && (
                  <button type="button" className="secondary" disabled={busy} onClick={() => void voidBill()}>
                    Void
                  </button>
                )}
                <button type="button" disabled={busy} onClick={() => void finalise()}>
                  Finalise bill
                </button>
              </div>
            )}
            {invoice.status === 'draft' && (
              <p className="panel-note">A draft cannot take payment. Finalise it to start collecting.</p>
            )}
          </>
        )}

        {(payments.length > 0 || refunds.length > 0) && (
          <>
            <h4 style={{ margin: '16px 0 6px' }}>Received</h4>
            <div className="table-wrap">
              <table className="records-table">
                <thead>
                  <tr>
                    <th>Receipt</th><th>When</th><th>Mode</th>
                    <th className="num">Amount</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const back = refunds
                      .filter((r) => r.paymentId === p.id)
                      .reduce((s, r) => s + Math.max(0, -Number(r.amount)), 0)
                    const left = Math.round((Number(p.amountPaid) - back) * 100) / 100
                    return (
                      <tr key={p.id}>
                        <td className="mono">{p.receiptNo}</td>
                        <td className="nowrap">{fmtDateTime(p.collectedAt)}</td>
                        <td className="nowrap">
                          {PAYMENT_MODE_LABEL[p.mode]}
                          {p.referenceNo && <div className="rec-code">{p.referenceNo}</div>}
                        </td>
                        <td className="num">
                          {rupees(p.amountPaid)}
                          {back > 0 && <div className="rec-code">less {rupees(back)} refunded</div>}
                        </td>
                        <td className="nowrap">
                          {canRefund && left > 0 && (
                            <button type="button" className="link" onClick={() => {
                              setRefundOf(p.id); setRefundAmount(left.toFixed(2)); setRefundReason('')
                            }}>Refund</button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {refunds.length > 0 && (
              <ul className="panel-note" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {refunds.map((r) => (
                  <li key={r.id}>
                    {rupees(Math.abs(Number(r.amount)))} refunded {fmtDateTime(r.createdAt)}
                    {r.reason ? ` — ${r.reason}` : ''}
                  </li>
                ))}
              </ul>
            )}

            <div className="bill-total-row">
              <span>Received {rupees(totals.paid)}</span>
              {Number(totals.refunded) > 0 && <span>refunded {rupees(totals.refunded)}</span>}
              <strong>
                {outstanding > 0 ? `Outstanding ${rupees(outstanding)}` : 'Settled in full'}
              </strong>
            </div>
          </>
        )}

        {refundOf && (
          <form onSubmit={submitRefund} className="bill-collect">
            <h4>Refund</h4>
            <div className="grid two">
              <div className="field">
                <label htmlFor="ref-amt">Amount</label>
                <input id="ref-amt" type="number" min="0.01" step="0.01" value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="ref-why">Reason</label>
                <input id="ref-why" value={refundReason} required
                  onChange={(e) => setRefundReason(e.target.value)} />
              </div>
            </div>
            <p className="panel-note">
              The receipt is never altered. The refund is recorded against it as a separate
              entry and the balance reopens by that much.
            </p>
            <div className="actions">
              <button type="button" className="secondary" onClick={() => setRefundOf(null)}>Cancel</button>
              <button type="submit" disabled={busy}>Record refund</button>
            </div>
          </form>
        )}

        {canTakeMoney && !editing && (
          <form onSubmit={takePayment} className="bill-collect">
            <h4>Take a payment</h4>
            <div className="grid three">
              <div className="field">
                <label htmlFor="pay-amt">Amount</label>
                <input id="pay-amt" type="number" min="0.01" step="0.01" max={outstanding}
                  value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="pay-mode">Mode</label>
                <select id="pay-mode" value={mode} onChange={(e) => setMode(e.target.value as PaymentMode)}>
                  {COUNTER_MODES.map((m) => (
                    <option key={m} value={m}>{PAYMENT_MODE_LABEL[m]}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="pay-ref">
                  {mode === 'cash' ? 'Cash received' : 'Reference no.'}
                </label>
                {mode === 'cash'
                  ? (
                    <input id="pay-ref" type="number" min="0" step="0.01" value={cashReceived}
                      placeholder="What the patient handed over"
                      onChange={(e) => setCashReceived(e.target.value)} />
                  )
                  : (
                    <input id="pay-ref" value={reference} placeholder="UTR, approval code, cheque no."
                      onChange={(e) => setReference(e.target.value)} />
                  )}
              </div>
            </div>

            {change != null && change >= 0 && (
              <p className="panel-note">Change to hand back: <strong>{rupees(change)}</strong></p>
            )}
            {change != null && change < 0 && (
              <p className="panel-note">That is less than the amount being paid.</p>
            )}
            {Number(amount) < outstanding && Number(amount) > 0 && (
              <p className="panel-note">
                A part payment. {rupees(outstanding - Number(amount))} would remain outstanding.
              </p>
            )}

            <div className="actions">
              <button type="button" className="secondary" onClick={() => setAmount(totals.outstanding)}>
                Pay it all
              </button>
              <button type="submit" disabled={busy}>
                {busy ? 'Recording…' : `Take ${rupees(amount || 0)}`}
              </button>
            </div>
          </form>
        )}

        <div className="actions" style={{ marginTop: 16 }}>
          <button type="button" className="secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
